// It would be nice if we could specify the module name dynamically,
// but we actually can't. All we can do is receive a function that
// performs the import.

import { logError } from "common/debug-print";
import { getFeatureSettingProbability } from "common/feature-settings";
import { maybeFrontendTrace } from "common/tracing";
import { getIsOnline } from "hooks/use-network-status";
import { reloadBrowserWindow } from "./browser-reload";
import { waitForInstalledServiceWorker } from "./register-service-worker";

// Reproduced here because Support.ts is massive
// and we can't afford the import
function sleep(time: number, signal?: AbortSignal) {
    return new Promise(resolve => {
        const sleepTimeout = setTimeout(resolve, time);
        signal?.addEventListener("abort", () => clearTimeout(sleepTimeout));
    });
}

let reloadLoopPromise: Promise<void> | undefined;
const reloadSleep = 10000;
// This should be the same as the Service Worker.
const timeout = 60000;

export class LazyLoadError extends Error {}

export async function lazyLoading<T>(
    assetName: string,
    canFailIfOffline: boolean,
    loader: () => Promise<T>
): Promise<T> {
    const abortController = new AbortController();
    let timedOut = false;

    // Safari will stall requests if another service worker is installed
    // in the middle of the request. We need to wait for the service worker
    // to continue.
    await waitForInstalledServiceWorker();

    // Only report on 5% of the JavaScript loads; we want to reduce Honeycomb costs.
    const result = await maybeFrontendTrace(
        "lazyLoad",
        { assetName },
        getFeatureSettingProbability("traceLazyLoadingProbability"),
        async () => {
            try {
                const loadResult = await Promise.race([
                    loader().then(content => ({ kind: "loaded" as const, content })),
                    sleep(timeout, abortController.signal).then(() => ({ kind: "timeout" as const })),
                ]);
                abortController.abort();
                if (loadResult.kind === "loaded") return loadResult.content;
                timedOut = true;
                throw new Error(`Timed out after ${timeout} ms`);
            } catch (e: unknown) {
                logError(`Lazy-loading ${assetName}`, e);
                return undefined;
            }
        }
    );

    // If we can't actually load the asset, we're toast.
    // Let's bail and, if we're online, try all over again.
    if (result === undefined) {
        const failureReason = timedOut ? "timeout" : "network";
        if (canFailIfOffline && !getIsOnline()) {
            throw new LazyLoadError(
                `Failed to load asset (${failureReason}), and the browser appears offline. Load canceled.`
            );
        }

        if (reloadLoopPromise === undefined) {
            reloadLoopPromise = (async () => {
                while (true) {
                    reloadBrowserWindow(`Lazy load ${assetName} failed`);
                    await sleep(reloadSleep);
                }
            })();
        }
        // The promise itself loops forever, but we also need to loop forever,
        // actually just to make eslint happy.
        while (true) {
            await reloadLoopPromise;
        }
    }

    return result;
}
